import { Glue42Core } from "@glue42/core";

import { default as CallbackRegistryFactory, CallbackRegistry } from "callback-registry";

import manager from "./items/Manager";
import { createEmail } from "./methods/createEmail";
import { createLocalEmailMethod } from "./methods/createLocalEmail";
import { createTask } from "./methods/createTask";
import { registerAgmMethods } from "./registerAgmMethods";
import { handleTrackingItems } from "./methods/handleTrackingItems";
import { itemFromJSON } from "./methods/itemFromJSON";
import {
    Glue42Office,
} from "../../glue4office";
import {
    connected,
    registerDisplaySecureEmail,
    registerEventListeners,
    registerOnAddinStatusChanged,
    registerOnEmailReceived,
    registerOnTaskCreated,
    registerSecureReply,
    registerTrackEmail,
    registerTrackItem,
    registerUntrackEmail,
    registerUntrackItem,
} from "./registerEventListeners";
import {
    OnAddinStatusChangedCallback,
    OnDisplaySecureEmailCallback,
    OnEmailReceivedCallback,
    OnSecureReplyCallback,
    OnTaskCreatedCallback,
    TrackCalendarEventCallback,
    TrackEmailCallback,
    UntrackCalendarEventCallback,
    UntrackEmailCallback,
    Config,
} from "./types";

export default function(config: Config) {
    let outlook: Glue42Office.Outlook.API;
    if (!config.agm) {
        throw Error("config.agm is required");
    }

    const callbacks: CallbackRegistry = CallbackRegistryFactory();
    const agm: Glue42Core.AGM.API = config.agm;
    manager.init(agm);

    registerAgmMethods(agm, callbacks);
    registerEventListeners(agm, callbacks);

    const onAddinStatusChanged = (callback: OnAddinStatusChangedCallback): () => void => {
        return registerOnAddinStatusChanged(agm, callback, callbacks);
    };

    const onEmailReceived = (callback: OnEmailReceivedCallback) => {
        return registerOnEmailReceived(agm, callback, callbacks);
    };
    const onTaskCreated = (callback: OnTaskCreatedCallback) => {
        return registerOnTaskCreated(agm, callback, callbacks);
    };

    const onTrackEmail = (callback: TrackEmailCallback) => {
        return registerTrackEmail(agm, callback, callbacks);
    };
    const onUntrackEmail = (callback: UntrackEmailCallback) => {
        return registerUntrackEmail(agm, callback, callbacks);
    };

    const onTrackCalendarEvent = (callback: TrackCalendarEventCallback) => {
        return registerTrackItem(agm, callback, callbacks);
    };
    const onUntrackCalendarEvent = (callback: UntrackCalendarEventCallback) => {
        return registerUntrackItem(agm, callback, callbacks);
    };

    const onSecureReply = (callback: OnSecureReplyCallback) => {
        return registerSecureReply(agm, callback, callbacks);
    };

    const onDisplaySecureEmail = (callback: OnDisplaySecureEmailCallback) => {
        return registerDisplaySecureEmail(agm, callback, callbacks);
    };

    const ready = (): Promise<Glue42Office.Outlook.API> => new Promise((resolve) => {
        resolve(outlook);
    });

    // TODO add action for test purposes
    // const newEmail = (emailParams: EmailParams = {}, options: NewEmailOptions, action?: number): Promise<void> =>
    //     createEmail(agm, emailParams, options, action);
    const newEmail = (emailParams: Glue42Office.Outlook.EmailParams = {}, options: Glue42Office.Outlook.NewEmailOptions): Promise<void> =>
        createEmail(agm, emailParams, options);
    const trackCalendarEvent = (event: Glue42Office.Outlook.T42Appointment | Glue42Office.Outlook.T42Meeting, conversationId?: Glue42Office.Outlook.T42Id):
        Promise<{ event: Glue42Office.Outlook.T42Appointment | Glue42Office.Outlook.T42Meeting, conversationIds: Glue42Office.Outlook.T42Id[] }> =>
        handleTrackingItems(agm, "track", event, conversationId);
    const untrackCalendarEvent = (event: Glue42Office.Outlook.T42Appointment | Glue42Office.Outlook.T42Meeting): Promise<{ event: Glue42Office.Outlook.T42Appointment | Glue42Office.Outlook.T42Meeting }> =>
        handleTrackingItems(agm, "untrack", event);
    const createLocalEmail = (localEmailParams: Glue42Office.Outlook.LocalEmailParams): Promise<Glue42Office.Outlook.Email> =>
        createLocalEmailMethod(agm, localEmailParams);
    const newTask = (taskParams: Glue42Office.Outlook.TaskParams = {}, options: Glue42Office.Outlook.NewTaskOptions): Promise<void> =>
        createTask(agm, taskParams, options);
    const emailFromJSON = (email: Glue42Office.Outlook.T42Email): Glue42Office.Outlook.Email => itemFromJSON(email, agm, "email");
    const taskFromJSON = (task: Glue42Office.Outlook.T42Task): Glue42Office.Outlook.Task => itemFromJSON(task, agm, "task");
    const attachmentFromJSON = (attachment: Glue42Office.Outlook.T42Attachment): Glue42Office.Outlook.Attachment => itemFromJSON(attachment, agm, "attachment");
    const showEmail = (ids: Glue42Office.Outlook.T42Id[]): Promise<Glue42Office.Outlook.T42Id[]> =>
        manager.showItemMethod(ids, "showEmail");
    const showTask = (ids: Glue42Office.Outlook.T42Id[]): Promise<Glue42Office.Outlook.T42Id[]> =>
        manager.showItemMethod(ids, "showTask");

    outlook = {
        ready,
        newEmail,
        trackCalendarEvent,
        untrackCalendarEvent,
        createLocalEmail,
        newTask,
        emailFromJSON,
        taskFromJSON,
        attachmentFromJSON,
        showEmail,
        showTask,

        onAddinStatusChanged,
        onEmailReceived,
        onTaskCreated,
        onTrackEmail,
        onUntrackEmail,
        onTrackCalendarEvent,
        onUntrackCalendarEvent,
        onSecureReply,
        onDisplaySecureEmail,
        get addinStatus() {
            return connected;
        },
    };

    return outlook;
}
